/**
 * The complete map of recordable meeting apps: every process identity we
 * recognize, keyed by the app's common name ("family"). Entries are macOS
 * bundle-ID prefixes and Windows executable basenames, matched by the web
 * app's `matchMeetingAppIdentifier`; a client only ever observes its own
 * platform's entries, so both live in one list per app. The family name is
 * the canonical capture-session identity everywhere downstream — session
 * grouping, recording bindings, per-app preferences — and the
 * `app_meetingMicAppAllowlist` flag default is generated from this map, so
 * the associations can't drift from the allowlist.
 *
 * A remote-config edit to the flag can still add a raw entry this map
 * doesn't know (the rapid response to a missing app); such an entry becomes
 * its own one-entry family under its literal value.
 */
export const MEETING_APP_FAMILIES = {
  chrome: ['com.google.chrome', 'chrome'],
  safari: ['com.apple.safari', 'com.apple.webkit'],
  edge: ['com.microsoft.edgemac', 'msedge'],
  firefox: ['org.mozilla.firefox', 'firefox'],
  arc: ['company.thebrowser.browser', 'arc'],
  brave: ['com.brave.browser', 'brave'],
  opera: ['com.operasoftware.opera', 'opera'],
  vivaldi: ['com.vivaldi.vivaldi', 'vivaldi'],
  zoom: ['us.zoom.xos', 'zoom'],
  teams: ['com.microsoft.teams', 'com.microsoft.teams2', 'ms-teams', 'teams'],
  slack: ['com.tinyspeck.slackmacgap', 'slack'],
  discord: ['com.hnc.discord', 'discord'],
  goto: ['com.logmein.goto', 'goto', 'g2mcomm'],
  webex: ['com.cisco.webexmeetingsapp', 'com.webex.meetingmanager', 'ciscocollabhost', 'atmgr', 'webex']
} as const satisfies Record<string, readonly string[]>

export type MeetingAppFamily = keyof typeof MEETING_APP_FAMILIES

export const BROWSER_MEETING_APP_FAMILIES: ReadonlySet<string> = new Set([
  'chrome',
  'safari',
  'edge',
  'firefox',
  'arc',
  'brave',
  'opera',
  'vivaldi'
] satisfies MeetingAppFamily[])

/**
 * Every registry family that is not a browser: an app whose capture can only
 * mean a call. Derived rather than listed so a family added to the registry
 * lands in exactly one of the two sets.
 */
export const NATIVE_MEETING_APP_FAMILIES: ReadonlySet<string> = new Set(
  Object.keys(MEETING_APP_FAMILIES).filter((family) => !BROWSER_MEETING_APP_FAMILIES.has(family))
)

export const defaultMeetingMicAppAllowlist = Object.values(MEETING_APP_FAMILIES)
  .flatMap((entries) => entries)
  .join(',')

/**
 * Hosts of the video-meeting providers we recognize in a join link. The
 * single source for both the web app's provider-URL preference when
 * extracting a link from event text (`calendarApi.ts`) and for treating a
 * link as join evidence at all (`expectationForAcceptedCapture`): an event's
 * extracted meetingLink can fall back to any URL found in its text, and an
 * agenda-doc link must not read as "this meeting runs in a browser".
 */
export const MEETING_PROVIDER_URL_PATTERN =
  /\b(?:zoom\.us|meet\.google\.com|teams\.(?:microsoft|live)\.com|webex\.com)\b/i

export function isMeetingProviderLink(link: string | undefined): boolean {
  return link !== undefined && MEETING_PROVIDER_URL_PATTERN.test(link)
}

/**
 * Providers whose join link launches a dedicated native app. Meet is absent
 * on purpose — it only runs in a browser, so a Meet link implies no native
 * family.
 */
const NATIVE_APP_LINK_PATTERNS: ReadonlyArray<{family: MeetingAppFamily; pattern: RegExp}> = [
  {family: 'zoom', pattern: /\bzoom\.us\b/i},
  {family: 'teams', pattern: /\bteams\.(?:microsoft|live)\.com\b/i},
  {family: 'webex', pattern: /\bwebex\.com\b/i}
]

/** The native app family a meeting link launches, or undefined for a browser-only (or unknown) provider. */
export function nativeFamilyForMeetingLink(link: string | undefined): MeetingAppFamily | undefined {
  if (link === undefined) return undefined
  return NATIVE_APP_LINK_PATTERNS.find(({pattern}) => pattern.test(link))?.family
}
